/**
 * Schedule data access (#303) — the cross-Booking window read behind the
 * Schedule timeline.
 *
 * One `GET /api/admin/schedule?from&to` call carries all four lenses:
 * Bookings (Jobs), Equipment gear lines, Vehicle bars, Crew bars. Per-day
 * numbers are aggregated client-side from the window-clipped gear lines so
 * the day cells and the peek panel read the same rows.
 */
import { apiFetch } from "../../../shared/api-client";
import { asBookingPhase, type BookingPhase } from "./booking-phase";

/** One Booking bar of the Jobs lens (and the date/phase source for bars). */
export interface ScheduleBooking {
  id: string;
  quote_number: string;
  event_name: string | null;
  client_name: string;
  event_date: string;
  event_end_date: string | null;
  quote_total: number | null;
  phase: BookingPhase;
  /**
   * The lifecycle Phase the reserving math uses; `phase` is the display
   * Phase (an Operator pin shows, matching the events list).
   */
  derived_phase: BookingPhase;
}

/**
 * One gear line inside the window, clipped to it. The reserving phases come
 * from `reserving_phases(company_id)` — the function the picker itself
 * resolves — so the Equipment lens shows the number `pick_gear_for_roles`
 * enforces. Quote-stage lines are returned whether or not they reserve;
 * `ScheduleResponse.quotes_hold_stock` says which (#344).
 */
export interface ScheduleEquipmentLine {
  equipment_id: string;
  equipment_name: string;
  stock_quantity: number | null;
  allow_double_book: boolean;
  booking_id: string;
  event_name: string | null;
  quote_number: string;
  client_name: string;
  quantity: number;
  starts_on: string;
  ends_on: string;
  derived_phase: BookingPhase;
}

export interface ScheduleVehicle {
  id: string;
  name: string;
  vehicle_type: string;
}

export interface ScheduleVehicleBar {
  vehicle_id: string;
  booking_id: string;
}

/**
 * One day of one truck being out (#305). Null times mean unknown, which the
 * lane draws as a whole-day block rather than inventing hours.
 */
export interface ScheduleVehicleWindow {
  vehicle_id: string;
  booking_id: string;
  window_date: string;
  start_time: string | null;
  end_time: string | null;
}

export interface ScheduleContractor {
  id: string;
  name: string;
}

export interface ScheduleCrewBar {
  contractor_id: string;
  booking_id: string;
  status: string;
  vehicle_name: string | null;
}

export interface ScheduleResponse {
  from: string;
  to: string;
  /**
   * Whether the `quote` Phase reserves Equipment for this Company (#314).
   * Quote holds count toward the Equipment lens' committed verdict only
   * when it is true; otherwise they are shown but do not grade.
   */
  quotes_hold_stock: boolean;
  bookings: ScheduleBooking[];
  equipment_lines: ScheduleEquipmentLine[];
  vehicles: ScheduleVehicle[];
  vehicle_bars: ScheduleVehicleBar[];
  vehicle_windows: ScheduleVehicleWindow[];
  contractors: ScheduleContractor[];
  crew_bars: ScheduleCrewBar[];
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function strOrNull(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function numOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  // The Go service emits numerics as strings; coerce defensively.
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function rec(raw: unknown): Record<string, unknown> | null {
  return typeof raw === "object" && raw !== null
    ? (raw as Record<string, unknown>)
    : null;
}

function normaliseBooking(raw: unknown): ScheduleBooking | null {
  const r = rec(raw);
  if (!r || typeof r.id !== "string") return null;
  const eventDate = str(r.event_date);
  if (eventDate === "") return null;
  return {
    id: r.id,
    quote_number: str(r.quote_number),
    event_name: strOrNull(r.event_name),
    client_name: str(r.client_name),
    event_date: eventDate,
    event_end_date: strOrNull(r.event_end_date),
    quote_total: numOrNull(r.quote_total),
    phase: asBookingPhase(r.phase) ?? "inquiry",
    derived_phase: asBookingPhase(r.derived_phase) ?? "inquiry",
  };
}

function normaliseLine(raw: unknown): ScheduleEquipmentLine | null {
  const r = rec(raw);
  if (
    !r ||
    typeof r.equipment_id !== "string" ||
    typeof r.booking_id !== "string"
  )
    return null;
  const startsOn = str(r.starts_on);
  const endsOn = str(r.ends_on);
  if (startsOn === "" || endsOn === "") return null;
  const quantity = numOrNull(r.quantity);
  if (quantity === null || quantity <= 0) return null;
  return {
    equipment_id: r.equipment_id,
    equipment_name: str(r.equipment_name),
    stock_quantity: numOrNull(r.stock_quantity),
    allow_double_book: r.allow_double_book === true,
    booking_id: r.booking_id,
    event_name: strOrNull(r.event_name),
    quote_number: str(r.quote_number),
    client_name: str(r.client_name),
    quantity,
    starts_on: startsOn,
    ends_on: endsOn,
    derived_phase: asBookingPhase(r.derived_phase) ?? "inquiry",
  };
}

function normaliseVehicle(raw: unknown): ScheduleVehicle | null {
  const r = rec(raw);
  if (!r || typeof r.id !== "string") return null;
  return { id: r.id, name: str(r.name), vehicle_type: str(r.vehicle_type) };
}

function normaliseVehicleBar(raw: unknown): ScheduleVehicleBar | null {
  const r = rec(raw);
  if (
    !r ||
    typeof r.vehicle_id !== "string" ||
    typeof r.booking_id !== "string"
  )
    return null;
  return { vehicle_id: r.vehicle_id, booking_id: r.booking_id };
}

function normaliseVehicleWindow(raw: unknown): ScheduleVehicleWindow | null {
  const r = rec(raw);
  if (
    !r ||
    typeof r.vehicle_id !== "string" ||
    typeof r.booking_id !== "string"
  )
    return null;
  const date = str(r.window_date);
  if (date === "") return null;
  return {
    vehicle_id: r.vehicle_id,
    booking_id: r.booking_id,
    window_date: date,
    start_time: strOrNull(r.start_time),
    end_time: strOrNull(r.end_time),
  };
}

function normaliseContractor(raw: unknown): ScheduleContractor | null {
  const r = rec(raw);
  if (!r || typeof r.id !== "string") return null;
  return { id: r.id, name: str(r.name) };
}

function normaliseCrewBar(raw: unknown): ScheduleCrewBar | null {
  const r = rec(raw);
  if (
    !r ||
    typeof r.contractor_id !== "string" ||
    typeof r.booking_id !== "string"
  )
    return null;
  return {
    contractor_id: r.contractor_id,
    booking_id: r.booking_id,
    status: str(r.status),
    vehicle_name: strOrNull(r.vehicle_name),
  };
}

function normaliseArray<T>(
  raw: unknown,
  one: (item: unknown) => T | null,
): T[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(one).filter((item): item is T => item !== null);
}

/** Fetch the schedule window. `from`/`to` are inclusive YYYY-MM-DD dates. */
export async function getSchedule(
  q: { from: string; to: string },
  signal?: AbortSignal,
): Promise<ScheduleResponse> {
  const data = await apiFetch<unknown>("/api/admin/schedule", {
    query: { from: q.from, to: q.to },
    signal,
  });
  const obj = rec(data);
  if (!obj) throw new Error("Expected schedule response object");
  return {
    from: str(obj.from),
    to: str(obj.to),
    // Absent or malformed reads as "quotes do not reserve" — the DB default,
    // and the reading that never claims a day is committed on holds the
    // picker would ignore.
    quotes_hold_stock: obj.quotes_hold_stock === true,
    bookings: normaliseArray(obj.bookings, normaliseBooking),
    equipment_lines: normaliseArray(obj.equipment_lines, normaliseLine),
    vehicles: normaliseArray(obj.vehicles, normaliseVehicle),
    vehicle_bars: normaliseArray(obj.vehicle_bars, normaliseVehicleBar),
    vehicle_windows: normaliseArray(
      obj.vehicle_windows,
      normaliseVehicleWindow,
    ),
    contractors: normaliseArray(obj.contractors, normaliseContractor),
    crew_bars: normaliseArray(obj.crew_bars, normaliseCrewBar),
  };
}
